import { getNote } from '@/lib/music/notes';
import { ledgerSteps, STAFF_LINE_STEPS, stepToY, TREBLE_CLEF_PATH } from '@/lib/music/staff';

interface StaffNotationProps {
  /** ids das notas, ex: ["C4"] ou ["C4","E4","G4"] */
  notes: string[];
  showClef?: boolean;
  timeSignature?: string | null;
  highlightIndex?: number;
  feedbackIndex?: number;
  feedbackType?: 'correct' | 'wrong' | null;
  showNoteNames?: boolean;
  space?: number;
  className?: string;
}

/**
 * Pentagrama com clave de sol desenhado corretamente.
 * A posição vertical de cada nota vem da biblioteca musical (linha de Sol = 0),
 * garantindo que Dó4 fique na linha suplementar e Ré4 no espaço abaixo da 1ª linha.
 */
export default function StaffNotation({
  notes,
  showClef = true,
  timeSignature = null,
  highlightIndex,
  feedbackIndex,
  feedbackType = null,
  showNoteNames = false,
  space = 24,
  className = '',
}: StaffNotationProps) {
  const noteSteps = notes.map((id) => getNote(id).step);
  const minStep = noteSteps.length > 0 ? Math.min(...noteSteps, -1) : -1;
  const maxStep = noteSteps.length > 0 ? Math.max(...noteSteps, 3) : 3;
  // A pauta original cabia até o Dó central. Para regiões mais graves/agudas,
  // ampliamos apenas o espaço necessário para linhas suplementares, evitando
  // cortar notas como C3 ou B5.
  const extraBelow = Math.max(0, -2 - minStep);
  const extraAbove = Math.max(0, maxStep - 4);
  const gLineY = space * (4.6 + extraAbove);
  const height = space * (8.6 + extraBelow + extraAbove);
  const leftPad = space * 0.7;
  const clefX = leftPad + space * 1.4;

  let cursorX = clefX + space * 3.0;
  let tsX: number | null = null;
  if (timeSignature) {
    tsX = clefX + space * 2.4;
    cursorX = tsX + space * 1.8;
  }

  const noteSpacing = space * 3.4;
  const notesStartX = cursorX;
  const count = Math.max(notes.length, 1);
  const width = notesStartX + count * noteSpacing + space * 1.2;

  const noteX = (index: number) => notesStartX + noteSpacing * (index + 0.5);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={`w-full h-auto ${className}`}
      role="img"
      aria-label={`Pentagrama na clave de sol com ${notes.length} nota(s)`}
    >
      {STAFF_LINE_STEPS.map((step) => (
        <line
          key={`line-${step}`}
          x1={0}
          x2={width}
          y1={stepToY(step, { gLineY, space })}
          y2={stepToY(step, { gLineY, space })}
          className="stroke-foreground-900"
          strokeWidth={Math.max(1.2, space * 0.075)}
        />
      ))}

      {showClef ? (
        <g transform={`translate(${clefX} ${gLineY}) scale(${space})`}>
          <path
            d={TREBLE_CLEF_PATH}
            fill="none"
            className="stroke-foreground-950"
            strokeWidth={0.22}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ) : null}

      {tsX !== null && timeSignature ? (
        <g className="fill-foreground-950 font-heading" textAnchor="middle">
          <text x={tsX} y={gLineY - space * 0.35} fontSize={space * 1.65} fontWeight={700}>
            {timeSignature.split('/')[0]}
          </text>
          <text x={tsX} y={gLineY + space * 1.55} fontSize={space * 1.65} fontWeight={700}>
            {timeSignature.split('/')[1]}
          </text>
        </g>
      ) : null}

      {notes.map((noteId, index) => {
        const note = getNote(noteId);
        const y = stepToY(note.step, { gLineY, space });
        const x = noteX(index);
        const isHighlight = highlightIndex === index;
        const fbType = feedbackIndex === index ? feedbackType : null;

        const headClass =
          fbType === 'correct'
            ? 'fill-accent-500'
            : fbType === 'wrong'
              ? 'fill-secondary-500'
              : isHighlight
                ? 'fill-primary-500'
                : 'fill-foreground-950';

        const rx = space * 0.62;
        const ry = space * 0.46;
        const stemDown = note.step >= 1;
        const stemX = stemDown ? x - rx * 0.92 : x + rx * 0.92;
        const stemEndY = stemDown ? y + space * 3.4 : y - space * 3.4;

        return (
          <g key={`note-${index}-${noteId}`}>
            {isHighlight ? (
              <circle cx={x} cy={y} r={space * 1.05} className="fill-primary-100" />
            ) : null}
            {ledgerSteps(note.step).map((lstep) => {
              const ly = stepToY(lstep, { gLineY, space });
              return (
                <line
                  key={`ledger-${index}-${lstep}`}
                  x1={x - space * 1.0}
                  x2={x + space * 1.0}
                  y1={ly}
                  y2={ly}
                  className="stroke-foreground-950"
                  strokeWidth={Math.max(1.4, space * 0.085)}
                />
              );
            })}
            <line
              x1={stemX}
              x2={stemX}
              y1={y}
              y2={stemEndY}
              className="stroke-foreground-950"
              strokeWidth={Math.max(1.4, space * 0.085)}
            />
            <ellipse
              cx={x}
              cy={y}
              rx={rx}
              ry={ry}
              transform={`rotate(-18 ${x} ${y})`}
              className={headClass}
            />
            {showNoteNames ? (
              <text
                x={x}
                y={height - space * 0.35}
                textAnchor="middle"
                className="fill-foreground-700 font-label"
                fontSize={space * 0.8}
                fontWeight={600}
              >
                {note.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}